// SplitViewLayout — présentation MASTER-DETAIL intra-onglet (prototype design JJ).
// ─────────────────────────────────────────────────────────────────────────────
// PRINCIPE : on NE recrée AUCUN contenu. Les composants de section EXISTANTS
// (CommercialSection, OperationSection, DALMobileBlock, DesktopDALWizard, DALForm…)
// sont rendus tels quels en `children`. Chaque bloc logique de ces composants est
// déjà balisé par un `data-subnav-id` (planté historiquement dans le code).
// SplitViewLayout se contente de :
//   1. lister à GAUCHE (~34%) un menu des sections présentes dans le DOM,
//   2. n'AFFICHER à DROITE (~66%) que le bloc `data-subnav-id` sélectionné
//      (les autres sont masqués via display:none — ils restent MONTÉS, donc
//       aucun state de saisie n'est perdu en changeant de section),
//   3. auto-déplier le <details>/BlockCard du bloc actif (confort lecture).
//
// Responsive (point de bascule = lg / 1024px) :
//   - <lg (mobile ~380px ET tablette ~768px) : menu EMPILÉ au-dessus, en barre
//     horizontale scrollable ; le panneau prend toute la largeur dessous. À 768px
//     un 34/66 côte-à-côte rendrait le contenu trop étroit → on reste empilé.
//   - ≥lg (1024px et +) : menu vertical ~34% + panneau ~66% côte à côte, pleine
//     largeur (le frame parent retire son max-w pour ces onglets).
// Tokens couleur var(--oh-*) uniquement (dual dark/light), zéro hex hardcodé.
import { useState, useRef, useLayoutEffect, useEffect } from "react";

// `onNavigateRef` (optionnel) : ref exposant `setActiveId` au parent pour piloter
// programmatiquement la section active (ex. « Modifier » depuis la consultation DAL
// → bascule sur « Assistant DAL »). N'altère pas les appelants existants.
export function SplitViewLayout({ sections = [], children, scanKey, ariaLabel = "Sections", onNavigateRef }) {
  const panelRef = useRef(null);
  const navRef = useRef(null);
  const [activeId, setActiveId] = useState(sections[0]?.id || null);
  const [presentIds, setPresentIds] = useState([]);

  // Expose la navigation programmatique au parent (setActiveId est stable).
  useEffect(() => {
    if (onNavigateRef) onNavigateRef.current = setActiveId;
    return () => { if (onNavigateRef && onNavigateRef.current === setActiveId) onNavigateRef.current = null; };
  }, [onNavigateRef]);

  // 1) Détecter les blocs réellement présents (certains sont conditionnels au
  //    statut du dossier — ex. Chantier, Financement). Le menu ne montre que ceux-là.
  useLayoutEffect(() => {
    const root = panelRef.current;
    if (!root) return;
    const found = new Set(
      Array.from(root.querySelectorAll("[data-subnav-id]")).map((el) =>
        el.getAttribute("data-subnav-id")
      )
    );
    const next = sections.filter((s) => found.has(s.id)).map((s) => s.id);
    setPresentIds((prev) =>
      prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next
    );
  }, [scanKey, sections]);

  const visibleSections = sections.filter((s) => presentIds.includes(s.id));
  const effectiveActive = presentIds.includes(activeId) ? activeId : presentIds[0] || null;

  // 2) N'afficher que le bloc actif ; masquer les autres (mais les garder montés).
  //    Auto-déplier le <details> du bloc actif pour montrer le contenu directement.
  useLayoutEffect(() => {
    const root = panelRef.current;
    if (!root) return;
    root.querySelectorAll("[data-subnav-id]").forEach((el) => {
      const id = el.getAttribute("data-subnav-id");
      const isActive = id === effectiveActive;
      el.style.display = isActive ? "" : "none";
      if (isActive) {
        const det = el.querySelector("details");
        if (det && !det.open) det.open = true;
      }
    });
  }, [effectiveActive, presentIds, scanKey]);

  // 3) Garder la pastille active visible quand le menu est en barre horizontale
  //    (mobile/tablette) — sinon elle peut être scrollée hors écran.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav || !effectiveActive) return;
    const btn = nav.querySelector(`[data-testid="split-view-item-${effectiveActive}"]`);
    if (btn) btn.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [effectiveActive, presentIds]);

  return (
    <div className="flex flex-col lg:flex-row gap-3 items-stretch" data-testid="split-view">
      {/* ── MENU (≥lg: vertical ~34% à gauche ; <lg: barre horizontale au-dessus) ── */}
      <nav
        ref={navRef}
        aria-label={ariaLabel}
        className="flex lg:flex-col gap-1.5 lg:w-[34%] lg:shrink-0 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0"
        data-testid="split-view-menu"
      >
        {visibleSections.map((s) => {
          const Icon = s.icon;
          const isActive = s.id === effectiveActive;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveId(s.id)}
              data-testid={`split-view-item-${s.id}`}
              aria-current={isActive ? "true" : undefined}
              aria-label={s.label}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-left whitespace-nowrap transition-colors shrink-0"
              style={{
                background: isActive ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)",
                border: `1px solid ${isActive ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`,
                color: isActive ? "var(--oh-stat-blue-text)" : "var(--oh-text-secondary)",
              }}
            >
              {Icon && (
                <Icon
                  className="w-4 h-4 shrink-0"
                  style={{ color: isActive ? "var(--oh-stat-blue-text)" : s.color || "var(--oh-text-muted)" }}
                />
              )}
              <span className="truncate">{s.label}</span>
            </button>
          );
        })}
      </nav>

      {/* ── PANNEAU (≥lg: ~66% à droite ; <lg: pleine largeur dessous) ── */}
      <div ref={panelRef} className="flex-1 min-w-0 lg:w-[66%]" data-testid="split-view-panel">
        {children}
      </div>
    </div>
  );
}

export default SplitViewLayout;
